import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useReducedMotion,
} from "framer-motion";

// ============================================================
// SIGNATURE: the connected ecosystem. Top systems feed a central
// database, then dashboard, then reports. Scroll activates nodes,
// draws connections, then data keeps flowing.
// ============================================================

const TOP = [
  { label: "Website", x: 110 },
  { label: "SaaS", x: 305 },
  { label: "CRM", x: 500 },
  { label: "AI", x: 695 },
  { label: "Automation", x: 890 },
];
const TOP_Y = 90;
const DB = { label: "Database", x: 500, y: 296 };
const DASH = { label: "Dashboard", x: 500, y: 452 };
const REP = { label: "Reports", x: 500, y: 592 };

const topPath = (x) => `M ${x} ${TOP_Y + 22} C ${x} 230, 500 190, 500 ${DB.y - 25}`;

const Node = ({ p, range, x, y, w = 150, h = 44, label, primary = false }) => {
  const opacity = useTransform(p, range, [0.1, 1]);
  const glow = useTransform(p, range, [0.1, 0.85]);
  return (
    <motion.g style={{ opacity }}>
      <rect
        x={x - w / 2}
        y={y - h / 2}
        width={w}
        height={h}
        rx="14"
        fill={primary ? "#1C7543" : "rgba(255,255,255,0.05)"}
        fillOpacity={primary ? 0.28 : 1}
        stroke="url(#sysGrad)"
        strokeWidth="1.4"
        strokeOpacity={glow}
      />
      <circle cx={x - w / 2 + 20} cy={y} r="3.4" fill="url(#sysGrad)" />
      <text
        x={x + 8}
        y={y + 4.5}
        textAnchor="middle"
        fill="#fff"
        fontSize="13"
        fontWeight="600"
        fontFamily="inherit"
      >
        {label}
      </text>
    </motion.g>
  );
};

const FlowPath = ({ p, range, d }) => {
  const pathLength = useTransform(p, range, [0, 1]);
  const opacity = useTransform(p, range, [0, 0.9]);
  return (
    <>
      <path d={d} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1.4" />
      <motion.path
        d={d}
        fill="none"
        stroke="url(#sysGrad)"
        strokeWidth="1.6"
        style={{ pathLength, opacity }}
      />
    </>
  );
};

export const SystemDiagram = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-25% 0px -25% 0px" });
  const reduceMotion = useReducedMotion();
  const { scrollYProgress: p } = useScroll({
    target: ref,
    offset: ["start 0.85", "end 0.55"],
  });

  return (
    <div ref={ref} data-testid="connected-system-diagram" className="relative mx-auto mt-14 w-full max-w-5xl">
      <svg
        viewBox="0 0 1000 640"
        className="w-full"
        role="img"
        aria-label="Connected business system: website, SaaS, CRM, AI and automation flow into a database, then a dashboard, then reports"
      >
        <defs>
          <linearGradient id="sysGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3CBE6E" />
            <stop offset="50%" stopColor="#1C7543" />
            <stop offset="100%" stopColor="#B8912A" />
          </linearGradient>
          <radialGradient id="sysGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1C7543" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#1C7543" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={DB.x} cy={DB.y} r="150" fill="url(#sysGlow)" />

        {TOP.map((n, i) => (
          <FlowPath key={n.label} p={p} range={[0.22 + i * 0.03, 0.46 + i * 0.03]} d={topPath(n.x)} />
        ))}
        <FlowPath p={p} range={[0.56, 0.68]} d={`M 500 ${DB.y + 25} L 500 ${DASH.y - 25}`} />
        <FlowPath p={p} range={[0.7, 0.82]} d={`M 500 ${DASH.y + 25} L 500 ${REP.y - 25}`} />

        {inView && !reduceMotion && (
          <>
            {TOP.map((n, i) => (
              <circle key={`sp-${n.label}`} r="3.4" fill="#B8912A" opacity="0.9">
                <animateMotion dur={`${3 + i * 0.4}s`} begin={`${i * 0.5}s`} repeatCount="indefinite" path={topPath(n.x)} />
              </circle>
            ))}
            <circle r="3.8" fill="#3CBE6E" opacity="0.95">
              <animateMotion dur="1.8s" repeatCount="indefinite" path={`M 500 ${DB.y + 25} L 500 ${DASH.y - 25}`} />
            </circle>
            <circle r="3.8" fill="#D3B74D" opacity="0.95">
              <animateMotion dur="1.8s" begin="0.9s" repeatCount="indefinite" path={`M 500 ${DASH.y + 25} L 500 ${REP.y - 25}`} />
            </circle>
          </>
        )}

        {TOP.map((n, i) => (
          <Node key={n.label} p={p} range={[0.02 + i * 0.05, 0.14 + i * 0.05]} x={n.x} y={TOP_Y} label={n.label} />
        ))}
        <Node p={p} range={[0.46, 0.6]} x={DB.x} y={DB.y} w={180} h={50} label={DB.label} primary />
        <Node p={p} range={[0.62, 0.74]} x={DASH.x} y={DASH.y} w={180} h={50} label={DASH.label} primary />
        <Node p={p} range={[0.78, 0.9]} x={REP.x} y={REP.y} w={180} h={50} label={REP.label} primary />
      </svg>

      <motion.p
        style={{ opacity: useTransform(p, [0.85, 1], [0, 1]) }}
        className="mt-6 text-center text-xs font-semibold uppercase tracking-[0.45em] text-transparent bg-clip-text bg-gradient-to-r from-[#3CBE6E] via-[#D3B74D] to-[#B8912A]"
      >
        One Connected Business System
      </motion.p>
    </div>
  );
};
